import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import { getStripe } from '../utils/stripe';
import SharedHeader from './SharedHeader';
import './GradingPlusSettings.css';

const MCP_URL = process.env.REACT_APP_COIN_MCP_URL || 'https://mcp.thecardboardcoin.com/mcp';
const money = cents => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format((Number(cents) || 0) / 100);
const date = seconds => seconds ? new Date(Number(seconds) * 1000).toLocaleDateString() : 'Shown on your next invoice';

async function api(path, options = {}) {
  const response = await fetch(path, { credentials: 'include', cache: 'no-store', ...options });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || 'The request could not be completed.');
  return data;
}

export default function GradingPlusSettings() {
  const { user, checkAuthStatus } = useAuth();
  const { coinMcpEnabled, gradingPlusCheckoutEnabled } = useFeatureFlags();
  const [quote, setQuote] = useState(null);
  const [connections, setConnections] = useState([]);
  const [csrf, setCsrf] = useState('');
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState('');
  const busyRef = useRef(false);
  const checkAuthRef = useRef(checkAuthStatus);
  checkAuthRef.current = checkAuthStatus;
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [invoiceUrl, setInvoiceUrl] = useState('');
  const [clock, setClock] = useState(Date.now());
  const active = status === true || user?.grading_plus_active === true;
  const paid = user?.is_subscribed === true;
  const lapsed = !paid && user?.sub_tier !== 'free';

  const refresh = useCallback(async () => {
    setError('');
    const results = await Promise.allSettled([
      api('/api/payment/grading-plus/status'),
      ...(coinMcpEnabled ? [api('/api/mcp/connections')] : []),
    ]);
    if (results[0].status === 'fulfilled') setStatus(results[0].value.grading_plus_active === true);
    else setError(results[0].reason.message);
    if (results[1]?.status === 'fulfilled') {
      setConnections(results[1].value.connections || []);
      setCsrf(results[1].value.csrf_token || '');
    } else if (results[1]?.status === 'rejected') setError(results[1].reason.message);
    await checkAuthRef.current();
  }, [coinMcpEnabled]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    const timer = setInterval(() => setClock(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const run = async (name, action) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(name); setError(''); setNotice('');
    try { await action(); }
    catch (failure) { setError(failure.message); }
    finally { busyRef.current = false; setBusy(''); }
  };

  const preview = () => run('preview', async () => {
    setQuote(null);
    setQuote(await api('/api/payment/grading-plus/preview', { method: 'POST' }));
  });
  const confirm = () => run('confirm', async () => {
    if (!quote || clock >= quote.expires_at * 1000) { setQuote(null); throw new Error('This quote expired. Preview the upgrade again.'); }
    const result = await api('/api/payment/grading-plus/confirm', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ confirm: true, quote_token: quote.quote_token }),
    });
    setQuote(null);
    setInvoiceUrl('');
    await refresh();
    if (result.grading_plus_active) setNotice('Grading+ is active. You can now connect an AI client.');
    else if (result.status === 'payment_pending') setNotice('Your payment is pending. Complete the invoice and refresh your status.');
    if (result.invoice_hosted_url && /^https:\/\//.test(result.invoice_hosted_url)) {
      setInvoiceUrl(result.invoice_hosted_url);
      setNotice('Your payment is pending. Open the invoice to complete it.');
    }
  });
  const checkout = () => run('checkout', async () => {
    const PLUS_PRICE_ID = process.env.REACT_APP_STRIPE_GRADING_PLUS_PRICE_ID;
    if (!PLUS_PRICE_ID) throw new Error('Grading+ checkout is not configured.');
    const result = await api('/api/payment/create-upgrade-session', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ price_id: PLUS_PRICE_ID }),
    });
    if (!result.sessionId) throw new Error('Checkout could not start.');
    const stripe = await getStripe();
    if (!stripe) throw new Error('Checkout is unavailable. Please try again.');
    const redirect = await stripe.redirectToCheckout({ sessionId: result.sessionId });
    if (redirect?.error) throw new Error(redirect.error.message || 'Checkout could not start.');
  });
  const revoke = connection => run(`revoke-${connection.id}`, async () => {
    await api(`/api/mcp/connections/${encodeURIComponent(connection.id)}/revoke`, {
      method: 'POST', headers: { 'X-CSRF-Token': csrf },
    });
    setNotice(`${connection.client_name} was disconnected.`);
    await refresh();
  });
  const copyEndpoint = () => run('copy', async () => {
    await navigator.clipboard.writeText(MCP_URL);
    setNotice('Endpoint copied.');
  });

  return <>
    <SharedHeader />
    <main className="grading-plus-settings">
      <div className="grading-plus-settings__intro">
        <Link to="/dashboard">← Back to dashboard</Link>
        <h1>AI connections</h1>
        <p>Use your COIN grading research in supported AI clients through a private, account linked connection.</p>
      </div>
      {error && <div className="grading-plus-settings__alert" role="alert">{error}</div>}
      {notice && <div className="grading-plus-settings__notice" role="status">{notice}</div>}
      {invoiceUrl && <p><a href={invoiceUrl} target="_blank" rel="noopener noreferrer">Open payment invoice</a></p>}
      <section className="grading-plus-settings__panel" aria-labelledby="plan-title">
        <h2 id="plan-title">Grading+ membership</h2>
        <p className="grading-plus-settings__price">$74.99 <span>/ month total</span></p>
        <p>Includes COIN grading and AI connection access. Card prices, grades and scores are research estimates, not guarantees.</p>
        {!gradingPlusCheckoutEnabled && !active && <p>Grading+ checkout is not available yet. Your current COIN access is unchanged.</p>}
        {active && <p role="status"><strong>Active.</strong> Your Grading+ access is ready.</p>}
        {gradingPlusCheckoutEnabled && !active && !paid && <>
          <p>{lapsed ? 'Your prior subscription has ended. Start a new monthly Grading+ subscription.' : 'Start a Grading+ monthly subscription using your current COIN account.'}</p>
          <button type="button" disabled={!!busy} onClick={checkout}>{busy === 'checkout' ? 'Opening checkout…' : 'Continue to Grading+ checkout'}</button>
        </>}
        {gradingPlusCheckoutEnabled && !active && paid && <>
          <p>Your existing paid subscription can be upgraded in place. Preview the exact credit and amount due before confirming.</p>
          <button type="button" disabled={!!busy} onClick={preview}>{busy === 'preview' ? 'Calculating…' : quote ? 'Refresh quote' : 'Preview upgrade'}</button>
          {quote && <div className="grading-plus-settings__quote" aria-label="Upgrade quote">
            <h3>Review your upgrade</h3>
            <dl>
              <div><dt>New monthly total</dt><dd>{money(quote.price_per_month_cents)}</dd></div>
              <div><dt>Unused time credit</dt><dd>−{money(quote.unused_time_credit_cents)}</dd></div>
              <div><dt>Due now</dt><dd><strong>{money(quote.amount_due_cents)}</strong></dd></div>
              <div><dt>Next billing date</dt><dd>{date(quote.next_billing_at)}</dd></div>
            </dl>
            {quote.annual_to_monthly && <p>Your annual subscription changes to monthly billing. The unused annual time is included in the credit above.</p>}
            <p>Quote expires at {new Date(quote.expires_at * 1000).toLocaleTimeString()}. If the amount changes, preview again.</p>
            <button type="button" disabled={!!busy || clock >= quote.expires_at * 1000} onClick={confirm}>{busy === 'confirm' ? 'Confirming…' : 'Confirm upgrade and charge amount due'}</button>
            {clock >= quote.expires_at * 1000 && <p role="status">This quote has expired. Refresh it before confirming.</p>}
          </div>}
        </>}
        <button className="grading-plus-settings__secondary" type="button" disabled={!!busy} onClick={() => run('refresh', refresh)}>Refresh membership status</button>
      </section>
      {coinMcpEnabled && <section className="grading-plus-settings__panel" aria-labelledby="connect-title">
        <h2 id="connect-title">Connect an AI client</h2>
        <p>Connect only a client you trust. It can request COIN research on your behalf after you approve the scopes on COIN’s consent page. Revoke access below at any time.</p>
        {!active && <p>Grading+ must be active before you can authorize a connection.</p>}
        <label htmlFor="coin-mcp-endpoint">COIN MCP endpoint</label>
        <div className="grading-plus-settings__endpoint"><input id="coin-mcp-endpoint" readOnly value={MCP_URL} /><button type="button" disabled={!!busy} onClick={copyEndpoint}>Copy</button></div>
        <p>In ChatGPT, Claude, or Gemini, add a remote MCP server using this endpoint and choose OAuth if prompted. Your client’s availability and account permissions may vary.</p>
        <ul><li>ChatGPT: open Plugins on the web, add a custom MCP server, enter the endpoint, and select OAuth.</li><li>Claude: open Customize → Connectors, add a custom connector, enter the endpoint, and sign in.</li><li>Gemini: on the web, open Settings → Connected Apps → Custom apps, enter the endpoint, and follow the sign-in steps.</li></ul>
        <p>When the client redirects you to COIN, sign in and review its requested access before approving. No COIN password or API key is entered into the AI client.</p>
      </section>}
      {coinMcpEnabled && <section className="grading-plus-settings__panel" aria-labelledby="connections-title">
        <h2 id="connections-title">Your connections</h2>
        {connections.filter(item => !item.revoked_at).length === 0 ? <p>No active connections.</p> : <ul className="grading-plus-settings__connections">{connections.filter(item => !item.revoked_at).map(item => <li key={item.id}>
          <div><strong>{item.client_name}</strong><span>Scopes: {item.scopes.join(', ') || 'None'}</span><span>Last used: {item.last_used_at ? new Date(item.last_used_at).toLocaleDateString() : 'Never'}</span></div>
          <button type="button" disabled={!!busy} onClick={() => revoke(item)} aria-label={`Disconnect ${item.client_name}`}>{busy === `revoke-${item.id}` ? 'Disconnecting…' : 'Disconnect'}</button>
        </li>)}</ul>}
        <p>Usage details are not available yet. Disconnecting one client leaves your other connections active.</p>
      </section>}
      <p className="grading-plus-settings__legal">AI clients receive only the COIN data you request through their tools. Their own privacy terms apply. <Link to="/privacy">Privacy</Link> · <Link to="/terms">Terms</Link></p>
    </main>
  </>;
}
